import { Box, Divider, Alert, Snackbar } from '@mui/material';
import PaymentsPreferences from './PaymentsPreferences';
import ScreeningPreferences from './ScreeningPreferences';
import {
  GenericProductDto,
  PackageV2Dto,
  PaymentsProductDto,
  PricingQuoteDetailsDto,
  PricingQuoteIncludedProductDto,
  ProductBillingTypeEnum,
  ProductTypeEnum,
  ProductV2Dto,
  ScreeningProductPricingDto,
  UpsertPricingQuoteDto,
} from '@inosio/payments-sdk';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { buildPricingQuoteDto, createPricingQuote, selectOnboardingApplicationState } from '../../../pmui/onboarding/sagas/pmonboardingSlice';
import { calculateCostFromTiers } from '../../../shared/util/calculators';
import ApplicationPreferences from './ApplicationPreferences';
import MaintenancePreferences from './MaintenancePreferences';
import OwnerPortalPreferences from './OwnerPortalPreferences';
import {
  ApplicationPreferencesValidator,
  MaintenancePreferencesValidator,
  OwnerPortalPreferencesValidator,
  PaymentsPreferencesValidator,
  ScreeningPreferencesValidator,
} from '../../validators/PricingValidators';
import { useHistory } from 'react-router';
import { PricingPageStates } from '../../PricingV2';
import { CoRequisiteProductDto } from '../../interfaces/ProductPreferenceInterface';
import { LoadingButton } from '@mui/lab';

export interface ChoosePreferencesProps {
  /**
   * The DTO for the selected package
   */
  selectedPackage: PackageV2Dto;

  /**
   * Active default products regardless of the package
   */
  defaultProducts: ProductV2Dto[];

  /**
   * Total number of units for this quote
   */
  doorCount: number;
}
const ChoosePreferences = (props: ChoosePreferencesProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { isCreatingPricingQuoteSuccess, PricingQuoteSetupObj, createPricingQuoteResponse, isCreatingPricingQuote, isCreatingPricingQuoteFailure } = useSelector(selectOnboardingApplicationState);
  //if the package does not have products included means its a custom package
  const isPackageCustom = props.selectedPackage?.PackageMetadata.IncludedProducts.length === 0;
  // if a package is selected then available products are from the package details, for custom its from the default product list
  const availableProductsList = isPackageCustom ? props.defaultProducts : props.selectedPackage.PackageMetadata.IncludedProducts;

  //local state
  const [availablePaymentsProducts, setAvailablePaymentsProducts] = useState<Array<PaymentsProductDto>>([]);
  const [availableScreeningProducts, setAvailableScreeningProducts] = useState<Array<ScreeningProductPricingDto>>([]);
  const [availableApplicationProducts, setAvailableApplicationProducts] = useState<Array<GenericProductDto>>([]);
  const [availableMaintenanceProducts, setAvailableMaintenanceProducts] = useState<Array<GenericProductDto>>([]);
  const [availableTenantPortalProducts, setAvailableTenantPortalProducts] = useState<Array<GenericProductDto>>([]);
  const [availableOwnerPortalProducts, setAvailableOwnerPortalProducts] = useState<Array<GenericProductDto>>([]);
  const [validationModeOn, setValidationModeOn] = useState(false);
  const [failureSnackBarOpen, setFailureSnackBarOpen] = useState(false);

  //validators
  const paymentsPreferncesValidator = new PaymentsPreferencesValidator(availableProductsList);
  const screeningPreferncesValidator = new ScreeningPreferencesValidator(availableProductsList);
  const applicationPreferencesValidator = new ApplicationPreferencesValidator(availableProductsList);
  const maintenancePreferencesValidator = new MaintenancePreferencesValidator(availableProductsList);
  const ownerPortalPreferencesValidator = new OwnerPortalPreferencesValidator(availableProductsList);

  //useffects

  /**
   * This useffect sets up our master pricing quote object with initial values
   */
  useEffect(() => {
    const totalPackageDiscount = calculateTotalDiscountForThisPackage();

    //fetch all products that were selected in our master object
    const existingSelectedProducts: PricingQuoteIncludedProductDto[] = PricingQuoteSetupObj.IncludedProducts || [];

    let newSelectedProductsList = [...existingSelectedProducts];

    //only initiliaze when you land for the first time from the choose package screen
    if (PricingQuoteSetupObj.IncludedProducts.length === 0) {
      //usually only a single product
      if (availableTenantPortalProducts.length > 0) {
        availableTenantPortalProducts.forEach((tenProd) => {
          const tenantPortalProduct: PricingQuoteIncludedProductDto = {
            ProductUuid: tenProd.ProductUuid,
            ProductType: ProductTypeEnum.TenantPortal,
            FlatFee: calculateCostFromTiers(tenProd.FlatFeeTiers || [], props.doorCount),
            ProductLabel: 'Tenant Portal',
          };

          // Add the new product in the selection list if it doesn't already exist
          if (!newSelectedProductsList.some((prod) => prod.ProductUuid === tenantPortalProduct.ProductUuid)) {
            newSelectedProductsList.push(tenantPortalProduct);
          }
        });
      }

      if (availableApplicationProducts.length > 0) {
        availableApplicationProducts.forEach((appProd) => {
          const appProduct: PricingQuoteIncludedProductDto = {
            ProductUuid: appProd.ProductUuid,
            ProductType: ProductTypeEnum.Applications,
            ProductLabel: 'Applications',
          };

          if (appProd.ProductBillingType === ProductBillingTypeEnum.FlatMonthlyFee) {
            appProduct.FlatFee = calculateCostFromTiers(appProd.FlatFeeTiers || [], props.doorCount);
            appProduct.ProductLabel = 'Application Portal';
          } else {
            appProduct.ConsumableFee = appProd.ConsumableFee;
            appProduct.ProductLabel = 'Application Consumable';
          }
          // Add the new product in the selection list if it doesn't already exist
          if (!newSelectedProductsList.some((prod) => prod.ProductUuid === appProduct.ProductUuid)) {
            newSelectedProductsList.push(appProduct);
          }
        });
      }

      if (availableScreeningProducts.length > 0) {
        /*
         * Screening is not a product itself so it doesnt have its own uuid.
         * We could have assigned a uuid of one of the screening type products but that would mean that the screening options would be preselected which we dont want.
         * However make sure before creating the pricing quote you select a valid screening product.
         */
        const screeningProduct: PricingQuoteIncludedProductDto = {
          ProductUuid: '',
          ProductType: ProductTypeEnum.Screening,
          ProductLabel: '',
        };

        // Add the new product in the selection list if it doesn't already exist
        if (!newSelectedProductsList.some((prod) => prod.ProductType === ProductTypeEnum.Screening)) {
          newSelectedProductsList.push(screeningProduct);
        }
      }

      if (availablePaymentsProducts.length > 0) {
        availablePaymentsProducts.forEach((payProd) => {
          //prepare the payments object to be added to selected products list
          const paymentsProduct: PricingQuoteIncludedProductDto = {
            ProductUuid: payProd.ProductUuid,
            ProductType: ProductTypeEnum.Payments,
            ProductLabel: 'Payments',
          };

          // Add the new product in the selection list if it doesn't already exist
          if (!newSelectedProductsList.some((prod) => prod.ProductUuid === paymentsProduct.ProductUuid)) {
            newSelectedProductsList.push(paymentsProduct);
          }
        });
      }

      //usually only a single product
      if (availableMaintenanceProducts.length > 0) {
        availableMaintenanceProducts.forEach((maintProd) => {
          //prepare the maintenance product object to be added to selected products list
          const maintenanceProduct: PricingQuoteIncludedProductDto = {
            ProductUuid: maintProd.ProductUuid,
            ProductType: ProductTypeEnum.Maintenance,
            FlatFee: calculateCostFromTiers(maintProd.FlatFeeTiers || [], props.doorCount),
            ProductLabel: 'Maintenance Coordination',
          };

          // Add the new product in the selection list if it doesn't already exist
          if (!newSelectedProductsList.some((prod) => prod.ProductUuid === maintenanceProduct.ProductUuid)) {
            newSelectedProductsList.push(maintenanceProduct);
          }
        });
      }

      //usually only a single product
      if (availableOwnerPortalProducts.length > 0) {
        availableOwnerPortalProducts.forEach((ownerProd) => {
          //prepare the owner portal product object to be added to selected products list
          const ownerPortalProduct: PricingQuoteIncludedProductDto = {
            ProductUuid: ownerProd.ProductUuid,
            ProductType: ProductTypeEnum.OwnerPortal,
            FlatFee: calculateCostFromTiers(ownerProd.FlatFeeTiers || [], props.doorCount),
            ProductLabel: 'Owner Portal',
          };

          // Add the new product in the selection list if it doesn't already exist
          if (!newSelectedProductsList.some((prod) => prod.ProductUuid === ownerPortalProduct.ProductUuid)) {
            newSelectedProductsList.push(ownerPortalProduct);
          }
        });
      }
    }

    dispatch({
      type: buildPricingQuoteDto.type,
      payload: {
        SelectedPackageUuid: props.selectedPackage.PackageUuid,
        SelectedPackageLabel: props.selectedPackage.PackageLabel,
        UnitCount: props.doorCount,
        SelectedPackageDiscount: totalPackageDiscount,
        IncludedProducts: newSelectedProductsList,
      } as PricingQuoteDetailsDto,
    });
  }, [
    props.selectedPackage,
    availableTenantPortalProducts,
    availableApplicationProducts,
    availableMaintenanceProducts,
    availablePaymentsProducts,
    availableScreeningProducts,
    availableOwnerPortalProducts,
  ]);

  /**
   * Listens to changes in the number of doors and updates the flatfee pricing for the following products
   *  - Tenant Portal
   *  - Owner Portal
   *  - Application Portal
   *  - Maintenance
   */
  useEffect(() => {
    // fetch all products that were selected in our master object
    const existingSelectedProducts: PricingQuoteIncludedProductDto[] = PricingQuoteSetupObj.IncludedProducts || [];

    // now get the flatfee products
    const applicationPortalProduct = availableApplicationProducts.find((prod) => prod.ProductBillingType === ProductBillingTypeEnum.FlatMonthlyFee);
    const tenantPortalProduct = availableTenantPortalProducts.find((prod) => prod.ProductBillingType === ProductBillingTypeEnum.FlatMonthlyFee);
    const maintenanceProduct = availableMaintenanceProducts.find((prod) => prod.ProductBillingType === ProductBillingTypeEnum.FlatMonthlyFee);
    const ownerPortalProduct = availableOwnerPortalProducts.find((prod) => prod.ProductBillingType === ProductBillingTypeEnum.FlatMonthlyFee);

    // create a map of product uuids to their calculated flat fees
    const productFlatFeeMap = new Map<string, number>();

    if (applicationPortalProduct) {
      productFlatFeeMap.set(applicationPortalProduct.ProductUuid, calculateCostFromTiers(applicationPortalProduct.FlatFeeTiers || [], props.doorCount));
    }
    if (tenantPortalProduct) {
      productFlatFeeMap.set(tenantPortalProduct.ProductUuid, calculateCostFromTiers(tenantPortalProduct.FlatFeeTiers || [], props.doorCount));
    }
    if (maintenanceProduct) {
      productFlatFeeMap.set(maintenanceProduct.ProductUuid, calculateCostFromTiers(maintenanceProduct.FlatFeeTiers || [], props.doorCount));
    }
    if (ownerPortalProduct) {
      productFlatFeeMap.set(ownerPortalProduct.ProductUuid, calculateCostFromTiers(ownerPortalProduct.FlatFeeTiers || [], props.doorCount));
    }

    // iterate over the selected products and update their FlatFee from the map
    const updatedSelectedProducts = existingSelectedProducts.map((product) => ({
      ...product,
      FlatFee: productFlatFeeMap.get(product.ProductUuid) || product.FlatFee,
    }));

    dispatch({
      type: buildPricingQuoteDto.type,
      payload: {
        SelectedPackageUuid: props.selectedPackage.PackageUuid,
        SelectedPackageLabel: props.selectedPackage.PackageLabel,
        UnitCount: props.doorCount,
        IncludedProducts: updatedSelectedProducts,
      } as PricingQuoteDetailsDto,
    });
  }, [props.doorCount]);

  /**
   * This useEffect sets the available products of each type based on the wizard's flow:
   * - If the package is custom, it fetches the products from the default product list (`activeProducts`).
   * - Otherwise, it retrieves the products from the selected package (`selectedPackage`).
   */
  useEffect(() => {
    const paymentsProducts = (availableProductsList.find((product) => product.ProductType === ProductTypeEnum.Payments)?.Payload as PaymentsProductDto[]) || [];
    const screeningProducts = (availableProductsList.find((product) => product.ProductType === ProductTypeEnum.Screening)?.Payload as ScreeningProductPricingDto[]) || [];
    const applicationsProducts = (availableProductsList.find((product) => product.ProductType === ProductTypeEnum.Applications)?.Payload as GenericProductDto[]) || [];
    const maintenanceProducts = (availableProductsList.find((product) => product.ProductType === ProductTypeEnum.Maintenance)?.Payload as GenericProductDto[]) || [];
    const tenantPortalProducts = (availableProductsList.find((product) => product.ProductType === ProductTypeEnum.TenantPortal)?.Payload as GenericProductDto[]) || [];
    const ownerPortalProducts = (availableProductsList.find((product) => product.ProductType === ProductTypeEnum.OwnerPortal)?.Payload as GenericProductDto[]) || [];

    setAvailablePaymentsProducts(paymentsProducts);
    setAvailableScreeningProducts(screeningProducts);
    setAvailableApplicationProducts(applicationsProducts);
    setAvailableMaintenanceProducts(maintenanceProducts);
    setAvailableOwnerPortalProducts(ownerPortalProducts);
    setAvailableTenantPortalProducts(tenantPortalProducts);
  }, [availableProductsList]);

  /**
   * Handle successfull creation of a pricing quote
   */
  useEffect(() => {
    if (isCreatingPricingQuoteSuccess && createPricingQuoteResponse) {
      history.push(`/pricing/${PricingPageStates.ViewQuote}?packageUuid=${props.selectedPackage?.PackageUuid}&doors=${props.doorCount}&quoteUuid=${createPricingQuoteResponse.Uuid}`);
    }
  }, [isCreatingPricingQuoteSuccess, createPricingQuoteResponse]);

  /**
   * Error handling
   */
  useEffect(() => {
    if (isCreatingPricingQuoteSuccess) {
      setFailureSnackBarOpen(false);
    } else if (isCreatingPricingQuoteFailure) {
      setFailureSnackBarOpen(true);
    }
  }, [isCreatingPricingQuoteFailure, isCreatingPricingQuoteSuccess]);

  //handlers

  const handleContinue = () => {
    setValidationModeOn(true);

    // Validate Payments Preferences if payments product is in the package
    if (availablePaymentsProducts) {
      const paymentsPreferences = PricingQuoteSetupObj.IncludedProducts.find((prod) => prod.ProductType === ProductTypeEnum.Payments);
      if (!paymentsPreferncesValidator.IsPreferenceObjectComplete(paymentsPreferences)) {
        return;
      }
    }

    // Validate Screening Preferences if screening product is in the package
    if (availableScreeningProducts?.length > 0) {
      const screeningPreferences = PricingQuoteSetupObj.IncludedProducts.find((prod) => prod.ProductType === ProductTypeEnum.Screening);
      if (!screeningPreferncesValidator.IsPreferenceObjectComplete(screeningPreferences)) {
        return;
      }
    }

    // Validate Application Preferences if apps product is in the package
    if (availableApplicationProducts?.length > 0) {
      const applicationPreferences = PricingQuoteSetupObj.IncludedProducts.filter((prod) => prod.ProductType === ProductTypeEnum.Applications);
      if (!applicationPreferences.every((appPref) => applicationPreferencesValidator.IsPreferenceObjectComplete(appPref))) {
        return;
      }
    }

    // Validate Owner Portal Preferences if owner portal product is in the package
    if (availableOwnerPortalProducts) {
      const ownerPortalPreferences = PricingQuoteSetupObj.IncludedProducts.find((prod) => prod.ProductType === ProductTypeEnum.OwnerPortal);
      if (!ownerPortalPreferencesValidator.IsPreferenceObjectComplete(ownerPortalPreferences)) {
        return;
      }
    }

    // Validate Maintenance Preferences if maintenance product is in the package
    if (availableMaintenanceProducts) {
      const maintenancePreferences = PricingQuoteSetupObj.IncludedProducts.find((prod) => prod.ProductType === ProductTypeEnum.Maintenance);
      if (!maintenancePreferencesValidator.IsPreferenceObjectComplete(maintenancePreferences)) {
        return;
      }
    }

    // All validations passed, create the pricing quote
    const payload: UpsertPricingQuoteDto = {
      Payload: PricingQuoteSetupObj,
    };

    dispatch({
      type: createPricingQuote.type,
      payload: payload,
    });
  };

  /**
   * Calculates total discount for a given package
   * @returns
   */
  const calculateTotalDiscountForThisPackage = (): number => {
    let totalDiscountInMinorUnits = 0;

    if (props.selectedPackage.PackageMetadata?.Discounts?.length) {
      // Loop through each discount in the package
      props.selectedPackage.PackageMetadata.Discounts.forEach((discount) => {
        const discountTiers = discount.Tiers;

        // Calculate the applicable discount for the given door count
        const discountAmount = calculateCostFromTiers(discountTiers, props.doorCount);
        totalDiscountInMinorUnits += discountAmount;
      });
    }

    return totalDiscountInMinorUnits;
  };

  const handleAlertClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setFailureSnackBarOpen(false);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Divider sx={{ marginY: 1, borderBottomWidth: 0.3, backgroundColor: '#A8A8A8' }} />
      {availablePaymentsProducts.length > 0 && (
        <PaymentsPreferences
          isCustomMode={isPackageCustom}
          availableProducts={availablePaymentsProducts}
          validator={paymentsPreferncesValidator}
          inValidationMode={validationModeOn}
          preRequisiteProducts={[]}
          coRequisiteProducts={[
            ...availableApplicationProducts.map((appProd) => {
              const preReqProductObj: CoRequisiteProductDto = {
                ProductType: ProductTypeEnum.Applications,
                ProductUuid: appProd.ProductUuid,
              };

              return preReqProductObj;
            }),
            ...availableScreeningProducts.map((screeninProd) => {
              const preReqProductObj: CoRequisiteProductDto = {
                ProductType: ProductTypeEnum.Screening,
                ProductUuid: screeninProd.ProductUuid,
              };

              return preReqProductObj;
            }),
            //Its possible that apart from the available screening products this dummy object might also be present, remove it as well if payments turned off
            {
              ProductType: ProductTypeEnum.Screening,
              ProductUuid: '',
            } as CoRequisiteProductDto,

            ...availableOwnerPortalProducts.map((ownerProd) => {
              const preReqProductObj: CoRequisiteProductDto = {
                ProductType: ProductTypeEnum.OwnerPortal,
                ProductUuid: ownerProd.ProductUuid,
              };

              return preReqProductObj;
            }),
          ]}
        />
      )}
      {availableScreeningProducts?.length > 0 && availablePaymentsProducts.length > 0 && (
        <ScreeningPreferences
          preRequisiteProducts={[
            //apps prods
            ...availableApplicationProducts.map((appProd) => {
              const preReqProductObj: PricingQuoteIncludedProductDto = {
                ProductType: ProductTypeEnum.Applications,
                ProductUuid: appProd.ProductUuid,
                ProductLabel: 'Applications',
              };

              if (appProd.ProductBillingType === ProductBillingTypeEnum.FlatMonthlyFee) {
                preReqProductObj.FlatFee = calculateCostFromTiers(appProd.FlatFeeTiers || [], props.doorCount);
                preReqProductObj.ProductLabel = 'Application Portal';
              } else {
                preReqProductObj.ConsumableFee = appProd.ConsumableFee;
                preReqProductObj.ProductLabel = 'Application Consumable';
              }

              return preReqProductObj;
            }),
            //payments prod
            ...availablePaymentsProducts.map((payProd) => {
              const preReqProductObj: PricingQuoteIncludedProductDto = {
                ProductType: ProductTypeEnum.Payments,
                ProductUuid: payProd.ProductUuid,
                ProductLabel: 'Payments',
              };
              return preReqProductObj;
            }),
          ]}
          coRequisiteProducts={[]}
          isCustomMode={isPackageCustom}
          availableProducts={availableScreeningProducts}
          validator={screeningPreferncesValidator}
          inValidationMode={validationModeOn}
        />
      )}

      {availableApplicationProducts.length > 0 && availablePaymentsProducts.length > 0 && (
        <ApplicationPreferences
          isCustomMode={isPackageCustom}
          preRequisiteProducts={[
            //payments prod
            ...availablePaymentsProducts.map((payProd) => {
              const preReqProductObj: PricingQuoteIncludedProductDto = {
                ProductType: ProductTypeEnum.Payments,
                ProductUuid: payProd.ProductUuid,
                ProductLabel: 'Payments',
              };
              return preReqProductObj;
            }),
          ]}
          doorCount={props.doorCount}
          availableProducts={availableApplicationProducts}
          validator={applicationPreferencesValidator}
          inValidationMode={validationModeOn}
          coRequisiteProducts={[
            //screening prods
            ...availableScreeningProducts.map((screeninProd) => {
              const preReqProductObj: CoRequisiteProductDto = {
                ProductType: ProductTypeEnum.Screening,
                ProductUuid: screeninProd.ProductUuid,
              };

              return preReqProductObj;
            }),
            {
              ProductType: ProductTypeEnum.Screening,
              ProductUuid: '',
            } as CoRequisiteProductDto,
          ]}
        />
      )}
      {availableMaintenanceProducts.length > 0 && availableTenantPortalProducts.length > 0 && (
        <MaintenancePreferences
          isCustomMode={isPackageCustom}
          doorCount={props.doorCount}
          preRequisiteProducts={[
            ...availableTenantPortalProducts.map((payProd) => {
              const preReqProductObj: PricingQuoteIncludedProductDto = {
                ProductType: ProductTypeEnum.TenantPortal,
                ProductUuid: payProd.ProductUuid,
                ProductLabel: 'Tenant Portal',
              };
              return preReqProductObj;
            }),
          ]}
          availableProducts={availableMaintenanceProducts}
          validator={maintenancePreferencesValidator}
          inValidationMode={validationModeOn}
          coRequisiteProducts={[]}
        />
      )}
      {availableOwnerPortalProducts.length > 0 && availablePaymentsProducts.length > 0 && (
        <OwnerPortalPreferences
          isCustomMode={isPackageCustom}
          availableProducts={availableOwnerPortalProducts}
          preRequisiteProducts={[
            //payments prod
            ...availablePaymentsProducts.map((payProd) => {
              const preReqProductObj: PricingQuoteIncludedProductDto = {
                ProductType: ProductTypeEnum.Payments,
                ProductUuid: payProd.ProductUuid,
                ProductLabel: 'Payments',
              };

              return preReqProductObj;
            }),
          ]}
          doorCount={props.doorCount}
          validator={ownerPortalPreferencesValidator}
          inValidationMode={validationModeOn}
          coRequisiteProducts={[]}
        />
      )}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
        <LoadingButton
          variant="outlined"
          sx={{
            color: '#39A2DB',
            border: '2px solid #39A2DB',
            boxShadow: 'none',
            borderRadius: '24px',
            fontFamily: "'Open Sans', sans-serif",
            fontSize: '14px',
            fontWeight: 700,
            textTransform: 'capitalize',
            '&:hover': {
              backgroundColor: '#39A2DB',
              color: 'white',
              border: '2px solid #39A2DB',
            },
            width: '164px',
            height: '50px',
          }}
          onClick={handleContinue}
          loading={isCreatingPricingQuote}
        >
          Continue
        </LoadingButton>
      </Box>
      <Snackbar anchorOrigin={{ horizontal: 'center', vertical: 'top' }} open={failureSnackBarOpen} autoHideDuration={4000} onClose={handleAlertClose}>
        <Alert onClose={handleAlertClose} severity="error">
          {'Something went wrong while creating a pricing quote. Please try again later or contact support. Thanks'}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChoosePreferences;
